<template>
    <a-modal :visible="true" width="40vw" :footer="null" :closable="false" centered wrap-class-name="nvm-wrap" @cancel="$emit('close')">
        <div class="nvm-modal">
            <div class="nvm-head">
                <div>
                    <h3>{{ texts.title }}</h3>
                </div>
                <button class="nvm-x" @click="$emit('close')">
                    <span class="material-icons-outlined">close</span>
                </button>
            </div>

            <div class="nvm-body">
                <div class="nvm-label">Fornecedor</div>
                <a-select
                    :value="providerId"
                    show-search
                    :filter-option="filterProvider"
                    :loading="loadingProviders"
                    :not-found-content="loadingProviders ? 'Carregando...' : 'Nenhum fornecedor encontrado'"
                    :dropdown-style="{ minWidth: '500px' }"
                    placeholder="Busque pelo nome ou CNPJ"
                    style="width: 100%"
                    @change="selectProvider"
                >
                    <a-select-option v-for="provider in providers" :key="provider.value" :value="provider.value">
                        {{ provider.label }}<span v-if="provider.cnpj" class="nvm-option-sub"> · {{ provider.cnpj }}</span>
                    </a-select-option>
                </a-select>

                <div class="nvm-label nvm-gap">{{ sellerRequired ? "Vendedor" : "Vendedor (opcional)" }}</div>
                <a-select
                    v-model="sellerId"
                    show-search
                    :allow-clear="!sellerRequired"
                    :filter-option="filterOption"
                    :options="sellers"
                    :disabled="!providerId || !sellers.length"
                    :placeholder="sellerPlaceholder"
                    style="width: 100%"
                />

                <div class="nvm-label nvm-gap">Loja</div>
                <a-select
                    v-model="storeId"
                    show-search
                    :filter-option="filterOption"
                    :options="stores"
                    placeholder="Selecione a loja"
                    style="width: 100%"
                />
            </div>

            <a-alert v-if="error" type="error" show-icon class="nvm-error">
                <span slot="message">{{ error.message }}</span>
                <span v-if="error.visit" slot="description">
                    <a @click="openVisit(error.visit)">Ver a visita aberta</a>
                </span>
            </a-alert>

            <div class="nvm-foot">
                <a-button @click="$emit('close')">Cancelar</a-button>
                <a-button type="primary" :loading="saving" :disabled="!providerId || !storeId || (sellerRequired && !sellerId)" @click="save">{{ texts.confirm }}</a-button>
            </div>
        </div>
    </a-modal>
</template>

<script>
import VisitService from "@/services/v3/visits/visit.service";
import SellerOrderService from "@/services/v3/seller-orders/seller-order.service";
import ProviderService from "@/services/v3/provider.service";
import visitFormat from "../visit-format.mixin";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";

export default {
    name: "NewVisitModal",
    mixins: [visitFormat, loaderMixin],
    props: {
        stores: { type: Array, default: () => [] },
        kind: { type: String, default: "visit" },
    },
    data() {
        return {
            service: new VisitService(),
            providers: [],
            providerId: undefined,
            sellerId: undefined,
            storeId: this.stores.length === 1 ? this.stores[0].value : undefined,
            loadingProviders: false,
            saving: false,
            error: null,
        };
    },
    computed: {
        sellerRequired() {
            return this.kind !== "requisition";
        },
        sellers() {
            const provider = this.providers.find((item) => item.value === this.providerId);
            return provider ? provider.sellers : [];
        },
        sellerPlaceholder() {
            if (!this.providerId) return "Selecione o fornecedor primeiro";
            if (!this.sellers.length) return "Fornecedor sem vendedor";
            return this.sellerRequired ? "Selecione o vendedor" : "Sem vendedor";
        },
        texts() {
            return this.kind === "requisition"
                ? { title: "Nova requisição", confirm: "Criar requisição", done: "Requisição criada.", error: "Não foi possível criar a requisição." }
                : { title: "Nova visita", confirm: "Abrir visita", done: "Visita aberta.", error: "Não foi possível abrir a visita." };
        },
    },
    created() {
        this.loadProviders();
    },
    methods: {
        loadProviders() {
            this.loadingProviders = true;
            new ProviderService()
                .loadPurchaseProviders({ per_page: 1000, sort: "name", compact: 1 })
                .then(({ data }) => {
                    this.providers = (data.data || [])
                        .filter((provider) => !this.sellerRequired || (provider.sellers && provider.sellers.length))
                        .map((provider) => ({
                            value: provider.id,
                            label: provider.name,
                            cnpj: this.formatCnpj(provider.cnpj),
                            sellers: (provider.sellers || []).map((seller) => ({ value: seller.id, label: seller.name })),
                        }));
                })
                .catch((error) => this.notifyError(error, "Não foi possível carregar os fornecedores."))
                .finally(() => {
                    this.loadingProviders = false;
                });
        },
        selectProvider(providerId) {
            this.providerId = providerId;
            this.sellerId = this.sellers.length === 1 ? this.sellers[0].value : undefined;
        },
        formatCnpj(value) {
            const digits = String(value || "").replace(/\D/g, "");
            return digits.length === 14 ? digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5") : value || "";
        },
        filterProvider(input, option) {
            const provider = this.providers.find((item) => item.value === option.componentOptions.propsData.value);
            const term = input.toLowerCase();
            const digits = input.replace(/\D/g, "");
            if (!provider) return false;
            return provider.label.toLowerCase().includes(term) || (!!digits && provider.cnpj.replace(/\D/g, "").includes(digits));
        },
        filterOption(input, option) {
            const text = option.componentOptions.children[0].text || "";
            return text.toLowerCase().includes(input.toLowerCase());
        },
        openVisit(visit) {
            this.$emit("close");
            this.$router.push(`/visitas/${visit.id}`);
        },
        errorOf(error) {
            const data = error && error.response ? error.response.data : null;
            let message = null;
            if (data && Array.isArray(data.errors)) message = data.errors[0];
            else if (data && data.errors) message = Object.values(data.errors)[0][0];
            return {
                message: message || (data && data.message) || this.texts.error,
                visit: data && data.checkin ? data.checkin : null,
            };
        },
        save() {
            this.saving = true;
            this.error = null;
            const service = this.kind === "requisition" ? new SellerOrderService() : this.service;
            const payload = { seller_id: this.sellerId, store_id: this.storeId, ...(this.sellerRequired ? {} : { provider_id: this.providerId }) };
            service
                .create(payload)
                .then(({ data, status }) => {
                    this.snack(status === 201 ? this.texts.done : "Este vendedor já estava em visita nesta loja.");
                    this.$emit("created", data);
                    this.$emit("close");
                })
                .catch((error) => {
                    this.error = this.errorOf(error);
                })
                .finally(() => {
                    this.saving = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.nvm-modal {
    background: #fff;
    display: flex;
    flex-direction: column;
}
.nvm-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
        margin: 0;
    }
}
.nvm-x {
    width: 28px;
    height: 28px;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: #f0f0f0;
        color: #262626;
    }
}
.nvm-body {
    padding: 12px 20px 16px;
    border-top: 1px solid #f0f0f0;
}
.nvm-label {
    font-size: 13px;
    color: #262626;
    margin-bottom: 6px;
}
.nvm-gap {
    margin-top: 14px;
}
.nvm-option-sub {
    color: #8c8c8c;
}
.nvm-hint {
    font-size: 12px;
    color: #8c8c8c;
    margin-top: 6px;
}
.nvm-error {
    margin: 0 20px 16px;
}
.nvm-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
</style>

<style lang="scss">
.nvm-wrap {
    .ant-modal {
        min-width: 480px;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
